import React, { useCallback, useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Alert, Box, Button, CircularProgress, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { GlassCard, PersonAvatar, T, TYPE, formatDateTime, formatRelative } from "../../user/complaints/shared";
import { useSocketEvent } from "../../helpdesk/socket";
import { pm, errorMessage } from "../api";
import { usePm } from "../PmContext";
import { ACTIVITY_TEXT } from "../ui";
import { fieldSx } from "../inputs";

const TYPES = [["", "Everything"], ["issue", "Issues"], ["sprint", "Sprints"], ["release", "Releases"], ["member", "Members"], ["file", "Files"], ["workflow", "Workflow"], ["project", "Project"], ["worklog", "Work logs"]];

/** Project activity stream with filters (who, what, when) and paging. */
const Activity = ({ project, version }) => {
  const ctx = usePm();
  const [rows, setRows] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [filters, setFilters] = useState({ user: "", type: "", issue: "", from: "", to: "" });
  const [error, setError] = useState("");
  const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
  const load = useCallback(async (before) => {
    try {
      const r = await pm.activity(project._id, { ...params, before, limit: 50 });
      setRows((cur) => (before ? [...(cur || []), ...r.data] : r.data));
      setHasMore(r.hasMore);
      setError("");
    } catch (err) {
      setError(errorMessage(err));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project._id, JSON.stringify(params)]);
  useEffect(() => {
    load();
  }, [load, version]);
  useSocketEvent(["issue.created", "issue.updated", "issue.commented", "issue.status.changed", "sprint.started", "sprint.completed"], () => load());

  const days = [];
  for (const a of rows || []) {
    const d = new Date(a.createdAt).toDateString();
    if (!days.length || days[days.length - 1].d !== d) days.push({ d, items: [] });
    days[days.length - 1].items.push(a);
  }
  return (
    <Box>
      <Stack direction={{ xs: "column", md: "row" }} spacing={1.25} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <TextField select size="small" label="Person" value={filters.user} onChange={(e) => setFilters({ ...filters, user: e.target.value })} sx={{ ...fieldSx, minWidth: 170 }}>
          <MenuItem value="">Anyone</MenuItem>{ctx.people.map((p) => <MenuItem key={String(p.id)} value={String(p.id)}>{p.name}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Type" value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })} sx={{ ...fieldSx, minWidth: 140 }}>
          {TYPES.map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
        </TextField>
        <TextField size="small" label="Issue key" value={filters.issue} onChange={(e) => setFilters({ ...filters, issue: e.target.value.toUpperCase() })} sx={{ ...fieldSx, width: 130 }} />
        <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={filters.from} onChange={(e) => setFilters({ ...filters, from: e.target.value })} sx={fieldSx} />
        <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={filters.to} onChange={(e) => setFilters({ ...filters, to: e.target.value ? `${e.target.value}T23:59:59` : "" })} sx={fieldSx} />
      </Stack>
      {error && <Alert severity="error">{error}</Alert>}
      {!rows ? <CircularProgress size={22} /> : (
        <GlassCard sx={{ p: 2 }}>
          {!rows.length && <Typography sx={{ fontSize: TYPE.small, color: T.inkMuted }}>No activity matches.</Typography>}
          {days.map((g) => (
            <Box key={g.d} sx={{ mb: 2 }}>
              <Typography sx={{ fontSize: TYPE.micro, fontWeight: 700, color: T.inkMuted, mb: 1 }}>{g.d}</Typography>
              {g.items.map((a) => (
                <Stack key={a._id} direction="row" spacing={1.25} sx={{ py: 0.75 }} alignItems="flex-start">
                  <PersonAvatar name={a.actor?.name} size={26} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: TYPE.small }}>
                      {ACTIVITY_TEXT(a)}
                      {a.issueKey && <Typography component={RouterLink} to={`/projects/${project.key}/issues/${a.issueKey}`} sx={{ fontSize: TYPE.micro, ml: 1, color: T.brand }}>open</Typography>}
                    </Typography>
                    <Typography title={formatDateTime(a.createdAt)} sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>{formatRelative(a.createdAt)}</Typography>
                  </Box>
                </Stack>
              ))}
            </Box>
          ))}
          {hasMore && <Button size="small" onClick={() => load(rows[rows.length - 1]._id)}>Load older</Button>}
        </GlassCard>
      )}
    </Box>
  );
};

export default Activity;
